/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { CluTagUtterancesUiStore } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { getCluUtteranceService } from 'src/clu/core/services/utterances/CluUtteranceService';
import { getCluProjectExportMetaDataSwagger } from 'src/clu/modules/handlers/projects/cluProjectMappers';
import { fileService } from 'src/core/services/api/fileService';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export type CluUtteranceHandlerGroup = ReturnType<typeof createCluUtteranceHandlerGroup>;

/**
 * Creates a handler group of functions for the utterances to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createCluUtteranceHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const saveCluUtterances = async ({ localStore }: HandlerParams<{ localStore: CluTagUtterancesUiStore }>) => {
        const { currentCluProjectStore } = rootStore;

        const utteranceSwaggers = localStore.rawUtterancesJson.filter(u => u.dataset !== 'Generated').slice();

        await getCluUtteranceService().updateUtterances({
            utterancesData: utteranceSwaggers,
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        currentCluProjectStore.patchJsonData({ utterances: utteranceSwaggers });
    };

    const uploadCluUtterancesFromFile = async ({ files, dataset }: HandlerParams<{ files: File[]; dataset: DatasetType }>) => {
        const { currentCluProjectStore } = rootStore;

        const filesContent = await Promise.all(files.map(f => fileService.readFromDisk(f)));
        let utteranceSwaggers: CluUtteranceSwagger[];

        try {
            utteranceSwaggers = flatten(filesContent.map(content => <CluUtteranceSwagger[]>JSON.parse(content)));
        } catch {
            const error = new Error();
            error.name = 'Bad format';
            error.message = 'One or more files have incorrectly formatted JSON data.';

            throw error;
        }

        const utterancesData = currentCluProjectStore.jsonData.utterances.filter(
            utterance => !utteranceSwaggers.find(swagger => swagger.text === utterance.text)
        );
        const newUtterancesSwaggers = utteranceSwaggers.map(u => ({ ...u, dataset }));

        await getCluUtteranceService().updateUtterances({
            utterancesData: [...newUtterancesSwaggers, ...utterancesData],
            projectRequiredMetaData: getCluProjectExportMetaDataSwagger(currentCluProjectStore)
        });

        currentCluProjectStore.patchJsonData({ utterances: [...newUtterancesSwaggers, ...utterancesData] });
    };

    return { saveCluUtterances, uploadCluUtterancesFromFile };
};
